import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { listLoginAuditEvents } from "@/lib/admin.functions";
import { ShieldAlert } from "lucide-react";

export const Route = createFileRoute("/_admin/admin/login-audit")({
  component: LoginAuditPage,
});

type EventRow = {
  id: string;
  event_type: string;
  email: string | null;
  email_domain: string | null;
  ip: string | null;
  user_agent: string | null;
  failed_count: number | null;
  window_started_at: string | null;
  locked_until: string | null;
  reason: string | null;
  created_at: string;
};

const RANGE_OPTIONS = [
  { hours: 24, label: "Letzte 24 Std." },
  { hours: 24 * 7, label: "Letzte 7 Tage" },
  { hours: 24 * 30, label: "Letzte 30 Tage" },
] as const;

function eventBadge(type: string): string {
  switch (type) {
    case "lockout":
      return "bg-destructive/15 text-destructive";
    case "success_after_failures":
      return "bg-brand-wahlen/15 text-brand-wahlen";
    default:
      return "bg-secondary text-muted-foreground";
  }
}

function reasonLabel(reason: string | null): string {
  switch (reason) {
    case "invalid_credentials":
      return "Falsche Zugangsdaten";
    case "disallowed_domain":
      return "Unzulässige Domain";
    case "ip_rate_limit":
      return "IP-Limit erreicht";
    case "email_rate_limit":
      return "E-Mail-Limit erreicht";
    case "successful_login":
      return "Erfolgreich nach Fehlversuchen";
    // Ticket V-SK1
    case "no_internal_role":
      return "Kein interner Zugang";

    default:
      return reason ?? "—";
  }
}

function eventLabel(type: string): string {
  switch (type) {
    case "failure":
      return "Fehlversuch";
    case "lockout":
      return "Sperre";
    case "success_after_failures":
      return "Erfolg nach Fehlversuchen";
    default:
      return type;
  }
}

function LoginAuditPage() {
  const fetchEvents = useServerFn(listLoginAuditEvents);
  const [sinceHours, setSinceHours] = useState<number>(24 * 7);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["login-audit", sinceHours],
    queryFn: () => fetchEvents({ data: { sinceHours, limit: 200 } }),
  });

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-primary">
            Login-Audit
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Protokoll fehlgeschlagener Anmeldeversuche und automatischer Sperren am
            Backend-Login. Sichtbar ausschließlich für Administratoren.
          </p>
        </div>
        <ShieldAlert className="h-8 w-8 shrink-0 text-primary" aria-hidden />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt.hours}
            onClick={() => setSinceHours(opt.hours)}
            className={
              "rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors " +
              (sinceHours === opt.hours
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:bg-secondary")
            }
          >
            {opt.label}
          </button>
        ))}
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="ml-auto rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary disabled:opacity-60"
        >
          {isFetching ? "Aktualisiere…" : "Aktualisieren"}
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-secondary/60 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5">Zeit</th>
              <th className="px-3 py-2.5">Ereignis</th>
              <th className="px-3 py-2.5">E-Mail / Domain</th>
              <th className="px-3 py-2.5">IP</th>
              <th className="px-3 py-2.5">Versuche</th>
              <th className="px-3 py-2.5">Grund</th>
              <th className="px-3 py-2.5">Sperre bis</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  Lade Ereignisse…
                </td>
              </tr>
            )}
            {error && !isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-destructive">
                  Konnte Audit-Log nicht laden.
                </td>
              </tr>
            )}
            {!isLoading && !error && (data?.events ?? []).length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  Keine Ereignisse im gewählten Zeitraum.
                </td>
              </tr>
            )}
            {(data?.events ?? []).map((row: EventRow) => (
              <tr key={row.id} className="align-top">
                <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                  {new Date(row.created_at).toLocaleString("de-DE")}
                </td>
                <td className="px-3 py-2.5">
                  <span
                    className={
                      "inline-flex rounded px-2 py-0.5 text-xs font-semibold " +
                      eventBadge(row.event_type)
                    }
                  >
                    {eventLabel(row.event_type)}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  {row.email ?? (
                    <span className="text-muted-foreground">
                      —{row.email_domain ? ` (@${row.email_domain})` : ""}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs">
                  {row.ip ?? <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2.5 text-center">{row.failed_count ?? "—"}</td>
                <td className="px-3 py-2.5 text-xs">{reasonLabel(row.reason)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                  {row.locked_until
                    ? new Date(row.locked_until).toLocaleString("de-DE")
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Maximal 200 Einträge je Zeitraum. Eingaben mit unzulässiger E-Mail-Domain werden
        ohne den Localpart protokolliert, um keine privaten Adressen zu sammeln.
      </p>
    </div>
  );
}
